// CTV-431: drag-drop / click logo uploader. Mirrors the signed-upload-URL
// pattern used for creatives (POST to /stations/logo/upload-url, then PUT the
// file directly to Supabase Storage), so we don't need a multipart endpoint
// on the Fastify-based selfservice-api. On success we hand the public URL
// back to the parent which writes it into the form's `logoUrl` field — the
// existing URL input continues to render that value so admins can still
// paste a remote URL by hand.

import React from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { apiClient } from '../../../lib/apiClient';
import { ADMIN_UPLOAD_ENDPOINTS, type UploadEndpoints } from './uploadEndpoints';

const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/svg+xml',
  'image/webp',
];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB — matches the bucket limit.

interface LogoUploaderProps {
  onUploaded: (publicUrl: string) => void;
  /** Defaults to the authenticated admin routes; the intake passes its own. */
  endpoints?: UploadEndpoints;
}

interface UploadUrlResponse {
  uploadUrl: string;
  storagePath: string;
  token: string;
  publicUrl: string;
}

const LogoUploader: React.FC<LogoUploaderProps> = ({
  onUploaded,
  endpoints = ADMIN_UPLOAD_ENDPOINTS,
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [uploading, setUploading] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    setError(null);
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Please upload a PNG, JPG, GIF, SVG, or WebP image.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('File must be under 5MB.');
      return;
    }
    setUploading(true);
    try {
      const signed = await apiClient.post<UploadUrlResponse>(
        endpoints.logoUploadUrlPath,
        { filename: file.name, mimeType: file.type, sizeBytes: file.size },
        endpoints.anonymous ? { anonymous: true } : undefined,
      );
      const putRes = await fetch(signed.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putRes.ok) {
        throw new Error(`Storage PUT failed: ${putRes.status}`);
      }
      onUploaded(signed.publicUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) void upload(dropped);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) void upload(selected);
    e.target.value = '';
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={
          'rounded-xl border-2 border-dashed p-5 text-center cursor-pointer transition-colors ' +
          (isDragging ? 'border-slate-900 bg-slate-50' : 'border-slate-200 hover:border-slate-400')
        }
        aria-disabled={uploading}
      >
        <div className="flex items-center justify-center gap-2 text-[13px] text-slate-600">
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 text-slate-400" />
              Drop an image, or{' '}
              <span className="font-semibold text-slate-900 underline underline-offset-2 decoration-1">
                browse
              </span>
            </>
          )}
        </div>
        <p className="mt-1 text-[11px] text-slate-400 uppercase tracking-[0.08em] font-medium">
          PNG · JPG · GIF · SVG · WebP — up to 5 MB
        </p>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          onChange={handleInputChange}
          className="hidden"
        />
      </div>
      {error && <p className="text-xs text-red-500 mt-2 px-1">{error}</p>}
    </div>
  );
};

export default LogoUploader;
